import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Truck, Gauge, Fuel, AlertTriangle, ShieldCheck, Wrench, ArrowLeft, Loader2,
  Calendar, Activity, FileText,
} from "lucide-react";
import { cloudinaryThumb } from "@/lib/cloudinary";

function fmtDays(d) {
  if (d === null || d === undefined) return "—";
  if (d < 0) return `vencido ${-d}d`;
  if (d === 0) return "hoy";
  return `${d}d`;
}

export default function VehiculoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [veh, setVeh] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [partes, setPartes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/vehiculos/${id}`).then((r) => setVeh(r.data)).catch(() => setVeh(null)),
      api.get(`/flota/vehiculos/${id}/kpis`).then((r) => setKpis(r.data)).catch(() => setKpis(null)),
      api.get(`/flota/partes?vehiculo_id=${id}&limit=20`).then((r) => setPartes(r.data.items || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-500 inline" /></div>;
  if (!veh) return <div className="p-6 text-center text-zinc-500">Vehículo no encontrado. <Link to="/vehiculos" className="text-amber-400 hover:underline">Volver al listado</Link></div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="vehiculo-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/vehiculos")} data-testid="btn-volver">
          <ArrowLeft className="w-4 h-4 mr-1" /> Vehículos
        </Button>
      </div>

      {/* Cabecera */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 flex items-stretch gap-4">
        <div className="w-32 h-24 rounded overflow-hidden bg-zinc-950 flex items-center justify-center shrink-0">
          {veh.foto_url ? (
            <img src={cloudinaryThumb(veh.foto_url, 280, 180)} alt={veh.nombre} className="w-full h-full object-cover" />
          ) : (
            <Truck className="w-10 h-10 text-zinc-700" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold">{veh.nombre}</h1>
            {veh.matricula && <span className="text-sm font-mono text-zinc-400 bg-zinc-950 px-2 py-0.5 rounded">{veh.matricula}</span>}
          </div>
          <div className="text-xs text-zinc-400 mt-1">{veh.marca_modelo || veh.tipo} · {veh.propietario} {veh.capacidad_m3 ? `· ${veh.capacidad_m3} m³` : ""}</div>
          {veh.notas && <div className="text-xs text-zinc-500 mt-1 italic">{veh.notas}</div>}
          {kpis?.parte_abierto && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs bg-amber-500/20 text-amber-200 px-2 py-1 rounded animate-pulse">
              <Activity className="w-3 h-3" /> Jornada en curso · {kpis.parte_abierto.conductor_nombre} ({kpis.parte_abierto.numero})
            </div>
          )}
        </div>
      </div>

      {/* KPIs */}
      {kpis && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi icon={Gauge} label="Km totales" value={kpis.km_actuales?.toLocaleString() || "—"} unit="km" />
            <Kpi icon={Gauge} label="Km este mes" value={kpis.km_mes_actual?.toLocaleString() || "0"} unit="km" />
            <Kpi icon={Fuel} label="Repostado mes" value={kpis.litros_mes || 0} unit="L" />
            <Kpi icon={Fuel} label="€ combustible mes" value={kpis.importe_combustible_mes || 0} unit="€" />
            <Kpi icon={Activity} label="Consumo medio" value={kpis.consumo_medio_l100_mes != null ? kpis.consumo_medio_l100_mes : "—"} unit="L/100km" />
            <Kpi icon={AlertTriangle} label="Incidencias 12m" value={kpis.incidencias_12m || 0}
              color={kpis.incidencias_12m > 0 ? "rose" : "emerald"} />
            <Kpi icon={ShieldCheck} label="ITV" value={fmtDays(kpis.dias_para_itv)}
              color={kpis.alerta_itv ? "rose" : "emerald"} sub={veh.proxima_itv?.slice(0, 10)} />
            <Kpi icon={ShieldCheck} label="Seguro" value={fmtDays(kpis.dias_para_seguro)}
              color={kpis.alerta_seguro ? "rose" : "emerald"} sub={veh.vencimiento_seguro?.slice(0, 10)} />
          </div>

          {/* Mantenimiento programado */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><Wrench className="w-4 h-4 text-amber-400" /> Mantenimiento programado</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <Row label="Intervalo" value={kpis.intervalo_mantenimiento_km ? `${kpis.intervalo_mantenimiento_km.toLocaleString()} km` : "— (sin configurar)"} />
              <Row label="Próxima revisión a" value={kpis.km_proxima_revision ? `${kpis.km_proxima_revision.toLocaleString()} km` : "—"} />
              <Row label="Faltan"
                value={kpis.km_para_mantenimiento != null ? `${kpis.km_para_mantenimiento.toFixed(0)} km` : "—"}
                warn={kpis.alerta_mantenimiento} />
            </div>
            {kpis.alerta_mantenimiento && (
              <div className="mt-2 text-xs bg-amber-500/20 text-amber-200 rounded px-2 py-1.5">
                ⚠ Mantenimiento próximo. {kpis.km_para_mantenimiento <= 0 ? "Ya pasaste el km de revisión." : "Quedan menos de 1.000 km."}
              </div>
            )}
            {!kpis.intervalo_mantenimiento_km && (
              <div className="mt-2 text-xs text-zinc-500 italic">
                💡 Configura el intervalo de mantenimiento (ej. 15.000 km) editando el vehículo, así te avisamos antes.
              </div>
            )}
          </div>
        </>
      )}

      {/* Últimos partes */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-amber-400" /> Últimos partes ({partes.length})</h3>
          <Link to="/mantenimiento" className="text-xs text-amber-400 hover:text-amber-300">Ver todos →</Link>
        </div>
        {partes.length === 0 ? (
          <div className="text-xs text-zinc-500 italic text-center py-6">Sin partes registrados para este vehículo</div>
        ) : (
          <div className="space-y-1.5">
            {partes.map((p) => (
              <div key={p.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-xs flex items-center justify-between gap-2 flex-wrap"
                data-testid={`parte-row-${p.id}`}>
                <div>
                  <span className="font-mono text-amber-300">{p.numero}</span>
                  <span className="text-zinc-500 mx-1">·</span>
                  <span className="text-zinc-400">{p.fecha_salida?.slice(0, 10)}</span>
                  <span className="text-zinc-500 mx-1">·</span>
                  <span>{p.conductor_nombre}</span>
                  {p.uso === "manual" && <span className="ml-1.5 bg-cyan-500/30 text-cyan-200 text-[9px] uppercase px-1 py-0.5 rounded">manual</span>}
                  {p.incidencia_tuvo && <span className="ml-1.5 bg-rose-500/30 text-rose-200 text-[9px] uppercase px-1 py-0.5 rounded">incid.</span>}
                  {p.repostaje_hizo && <span className="ml-1.5 bg-emerald-500/30 text-emerald-200 text-[9px] uppercase px-1 py-0.5 rounded">repostó</span>}
                </div>
                <div className="text-zinc-300 font-bold">
                  {p.km_recorridos != null ? `${p.km_recorridos} km` : <span className="text-amber-400">ABIERTO</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Ico, label, value, unit, color, sub }) {
  const colors = {
    rose: "border-rose-500/40 bg-rose-500/5 text-rose-200",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-200",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-200",
  };
  return (
    <div className={`border rounded-lg p-3 ${colors[color] || "border-zinc-800 bg-zinc-900/60 text-zinc-200"}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70 flex items-center gap-1">
        <Ico className="w-2.5 h-2.5" /> {label}
      </div>
      <div className="text-2xl font-extrabold mt-0.5">{value} <span className="text-xs font-normal opacity-60">{unit}</span></div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

function Row({ label, value, warn }) {
  return (
    <div className={`bg-zinc-950 border ${warn ? "border-amber-500/50" : "border-zinc-800"} rounded px-3 py-2`}>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-sm font-bold ${warn ? "text-amber-300" : "text-zinc-200"}`}>{value}</div>
    </div>
  );
}
